"use client";

import { useWallet } from "@solana/wallet-adapter-react";
import { useEffect, useMemo, useRef } from "react";

import { checkTraderActivation } from "./traderActivation";
import { useAuthStore } from "./authStore";
import { useOnboardingStore } from "./onboarding/onboardingStore";
import { clearRiseSession, ensureRiseSession } from "./session";

const getMessage = (error: unknown): string =>
  error instanceof Error ? error.message : "Authentication failed";

export const AuthSensors = () => {
  const {
    connected,
    connecting,
    publicKey,
    signMessage,
    signTransaction,
    wallet,
  } = useWallet();
  const runIdRef = useRef(0);
  const publicKeyBase58 = useMemo(
    () => publicKey?.toBase58() ?? null,
    [publicKey]
  );
  const walletName = wallet?.adapter.name;

  useEffect(() => {
    runIdRef.current += 1;
    const runId = runIdRef.current;

    if (connecting) {
      useAuthStore.getState().setConnecting();
      return;
    }

    if (!connected || publicKeyBase58 === null) {
      const previousAuthority = useAuthStore.getState().authority;
      useAuthStore.getState().setUnauthenticated();
      if (previousAuthority !== null) void clearRiseSession();
      return;
    }

    if (signMessage === undefined && signTransaction === undefined) {
      useAuthStore
        .getState()
        .setAuthError(
          "This wallet does not support message or transaction signing."
        );
      return;
    }

    useAuthStore.getState().setSigning(publicKeyBase58);
    useOnboardingStore.getState().resetDismissed(publicKeyBase58);

    void (async () => {
      try {
        await ensureRiseSession(publicKeyBase58, {
          signMessage,
          signTransaction,
          walletName,
        });
        if (runIdRef.current !== runId) return;

        useAuthStore.getState().setAuthenticated(publicKeyBase58);
        useAuthStore.getState().setActivationChecking();

        const activation = await checkTraderActivation(publicKeyBase58);
        if (runIdRef.current !== runId) return;

        useAuthStore
          .getState()
          .setActivationStatus(
            activation.isPhoenixTrader ? "activated" : "notActivated"
          );
      } catch (error) {
        if (runIdRef.current !== runId) return;
        useAuthStore.getState().setAuthError(getMessage(error));
      }
    })();
  }, [
    connected,
    connecting,
    publicKeyBase58,
    signMessage,
    signTransaction,
    walletName,
  ]);

  return null;
};
